import React from 'react';
import { TFunction } from 'i18next';
import { Trans } from 'react-i18next';
import { ReactComponent as GoodServiceIcon } from 'assets/images/good_service.svg';
import { ReactComponent as ValueForMoneyIcon } from 'assets/images/value_for_money.svg';
import { ReactComponent as PurelyIcon } from 'assets/images/purely.svg';
import { ReactComponent as GreatAtmosphereIcon } from 'assets/images/great_atmosphere.svg';
import { ECountry, ratingValues } from 'common/const';
import { formatMoney } from 'common/helpers/amount.helper';
import { getTipsDocumentUrls } from 'common/helpers/documents.helper';
import { IOrganization } from 'common/types/organization.types';
import { ITipsFormData, TAmountError } from 'common/types/tips.types';

export const getServiceOptions = (t: TFunction<'translation', undefined>, formData: ITipsFormData) => {
  return [
    {
      label: t('payment.tips.goodService'),
      icon: <GoodServiceIcon />,
      name: 'comment_good_service',
      checked: formData.comment_good_service,
    },
    {
      label: t('payment.tips.valueForMoney'),
      icon: <ValueForMoneyIcon />,
      name: 'comment_tasty',
      checked: formData.comment_tasty,
    },
    {
      label: t('payment.tips.purely'),
      icon: <PurelyIcon />,
      name: 'comment_clean',
      checked: formData.comment_clean,
    },
    {
      label: t('payment.tips.greatAtmosphere'),
      icon: <GreatAtmosphereIcon />,
      name: 'comment_great_atmosphere',
      checked: formData.comment_great_atmosphere,
    },
  ];
};

/* Подпись чекбокса «покрыть комиссию» (US-034, R-6).
 *  RS — сегодняшняя «(15%)» байт-в-байт, фикс не упоминается (И1).
 *  ME — процент и фикс за вывод из withdrawal_fee: «(15% + 0.50 €)»; поля нет или фикс 0 — только процент.
 *  Сумму с комиссией страница не считает: её считает бэк (US-024, F-23). */
export const getCommissionLabel = (
  t: TFunction<'translation', undefined>,
  country: ECountry,
  commissionCoeff?: number,
  withdrawalFee?: number | null,
): string => {
  const coeff = commissionCoeff ? commissionCoeff * 100 : null;
  const label = t('payment.tips.feeForTransferringMoney');

  if (country !== ECountry.ME) {
    return `${label} ${coeff ? '(' + coeff + '%)' : ''}`;
  }

  const parts = [coeff ? `${coeff}%` : null, withdrawalFee ? formatMoney(withdrawalFee, country) : null].filter(Boolean);

  return `${label} ${parts.length ? '(' + parts.join(' + ') + ')' : ''}`;
};

/* Согласие перед оплатой по стране (US-034, R-8): ME — текст без сербской шифры платежа
 *  и ссылки на черногорские документы; RS — сегодняшний текст и адреса. */
const TIPS_TERMS_KEYS: Record<ECountry, string> = {
  [ECountry.RS]: 'payment.tips.tipsTermsAndConditions',
  [ECountry.ME]: 'payment.tips.tipsTermsAndConditionsMe',
};

export const getTipsAgreementItems = (
  t: TFunction<'translation', undefined>,
  formData: ITipsFormData,
  country: ECountry,
  organization?: IOrganization | null,
) => {
  const urls = getTipsDocumentUrls(country);

  const items = [];

  items.push({
    label: (
      <Trans
        i18nKey={TIPS_TERMS_KEYS[country]}
        values={{
          usloviKoriscenja: t('payment.tips.usloviKoriscenja'),
          ugovorOPoklonu: t('payment.tips.ugovorOPoklonu'),
          politikaPrivatnosti: t('payment.tips.politikaPrivatnosti'),
          kolacici: t('payment.tips.kolacici'),
          odustanakOdPoklona: t('payment.tips.odustanakOdPoklona'),
        }}
        components={{
          1: <a href={urls.usloviKoriscenja} target="_blank" rel="noopener noreferrer" />,
          2: <a href={urls.ugovorOPoklonu} target="_blank" rel="noopener noreferrer" />,
          3: <a href={urls.politikaPrivatnosti} target="_blank" rel="noopener noreferrer" />,
          4: <a href={urls.kolacici} target="_blank" rel="noopener noreferrer" />,
          5: <a href={urls.odustanakOdPoklona} target="_blank" rel="noopener noreferrer" />,
        }}
      />
    ),
    name: 'terms_and_conditions',
    checked: formData.terms_and_conditions,
  });

  items.push({
    label: getCommissionLabel(t, country, organization?.web_commission_coeff, organization?.withdrawal_fee),
    name: 'pay_commission_instead_of_waiter',
    checked: formData.pay_commission_instead_of_waiter,
  });

  return items;
};

export const getPresetOptions = (preset: number | null, country: ECountry, organization?: IOrganization | null) => {
  if (!organization) {
    return [];
  }

  return organization.tips_amount_options
    ?.filter((option) => option.value !== undefined)
    .map((option) => ({
      id: option.value?.toString(),
      label: formatMoney(option.value as number, country),
      value: option.value as number,
      checked: option.value === preset,
    }));
};

const AMOUNT_ERROR_KEYS: Record<TAmountError, string> = {
  min: 'payment.tips.enterAnAmountGreaterThan',
  max: 'payment.tips.enterAnAmountNotGreaterThan',
};

/* Текст ошибки суммы под полем: ниже минимума (US-034, R-5) или выше максимума страны
 *  (payment-method-rename, R-13). RS — «…60 dinara.», ME — «…2 €.» в формате подписей сумм. */
export const getAmountErrorText = (
  t: TFunction<'translation', undefined>,
  country: ECountry,
  error: TAmountError,
  organization?: IOrganization | null,
): string => {
  const limit = error === 'max' ? organization?.max_amount : organization?.min_amount;
  const amount = limit ? limit : 0;
  const text = t(AMOUNT_ERROR_KEYS[error]);

  if (country !== ECountry.ME) {
    return `${text}${amount}${t('payment.tips.dinara')}`;
  }

  return `${text}${formatMoney(amount, country)}.`;
};

/* Пресет по умолчанию — второй в списке (сегодняшнее поведение для стандартных списков, И1).
 *  Списка нет, он пуст или из одного элемента — предвыбора нет: гость вводит сумму вручную
 *  (US-030, критерий 8: без пресетов — без блока и без суммы по умолчанию). */
export const getDefaultPresetValue = (organization?: IOrganization | null): number | null =>
  organization?.tips_amount_options?.[1]?.value ?? null;

export const getRatingOptions = (rating?: number) => {
  return ratingValues.map((value) => ({
    id: value.toString(),
    value,
    checked: rating ? value === rating : false,
    isFilled: rating ? value <= rating : false,
  }));
};
